import React from 'react';
import { graphql, createFragmentContainer } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import { object, bool } from 'prop-types';
import { getIsNewSeller } from '../../utils/sellerHelpers';
import { getSrcsetString } from '../../utils/srcSet';

// components
import { PdpSharedStarRating } from '../PdpSharedStarRating/PdpSharedStarRating';
import { PdpSharedNewSellerPill } from '../PdpSharedNewSellerPill/PdpSharedNewSellerPill';
import { PdpSharedDealerDetailsFeedback } from '../PdpSharedDealerDetailsFeedback/PdpSharedDealerDetailsFeedback';
import { PdpSharedDealerDetailsBioModalLazy } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBioModalLazy';

import styles from './PdpSharedDealerDetailsHeader.scss';

const aboutSellerCopy = (
    <FormattedMessage
        id="PdpSharedDealerDetailsHeader.seller_title"
        defaultMessage="About the Seller"
    />
);

const shouldShowBioModal = seller => {
    let hasVideo = false;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const mediaList =
        seller?.liveStorefrontProfile?.galleryAlbum?.mediaList?.filter(media => {
            const element = media?.element;
            if (element.url) {
                hasVideo = true;
            }
            return element.path || element.url;
        }) || [];

    return !isRewardsTier2 && (hasVideo || mediaList.length > 1);
};

export function PdpSharedDealerDetailsHeaderComponent({
    user,
    relay,
    seller,
    isClient,
    inlineHeader = true,
}) {
    const { isRewardsTier2, shippingAddress, monthsSinceStartDate, liveStorefrontProfile } =
        seller || {};
    const canShowSellerName = user?.canShowSellerName;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const dealerLocation = shippingAddress?.displayFullCityStateCountry;

    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const headshot = (headshots && headshots[0]) || {};
    const { status: headshotStatus, path: headshotPath } = headshot;
    const showHeadshot = isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath;
    const mediaList = galleryAlbum?.mediaList || [];

    let detailsHeader;
    let sellerImagePath;

    if (showHeadshot) {
        sellerImagePath = headshotPath;
    } else if (mediaList.length) {
        sellerImagePath = mediaList.find(media => media?.element?.path)?.element?.path;
    }

    if (canShowSellerName) {
        detailsHeader = (
            <div className={styles.header}>
                <span className={styles.headerSecondary}>{aboutSellerCopy}</span>
                <div className={styles.headerPillContainer}>
                    <span
                        data-tn="pdp-shared-dealer-details-header"
                        className={styles.headerPrimary}
                    >
                        {seller?.sellerProfile?.company}
                    </span>
                    {isNewSeller && (
                        <span className={styles.pill}>
                            <PdpSharedNewSellerPill />
                        </span>
                    )}
                </div>
            </div>
        );
    } else {
        detailsHeader = (
            <span data-tn="pdp-shared-dealer-details-header" className={styles.headerPrimary}>
                {aboutSellerCopy}
                {isNewSeller && (
                    <span className={styles.pill}>
                        <PdpSharedNewSellerPill />
                    </span>
                )}
            </span>
        );
    }

    return (
        <>
            {!inlineHeader && <div className={styles.inlineHeader}>{detailsHeader}</div>}
            <div className={styles.container}>
                {sellerImagePath && (
                    <img
                        alt="" // decorative image
                        sizes="80px"
                        src={sellerImagePath}
                        className={styles.image}
                        srcSet={getSrcsetString(sellerImagePath)}
                    />
                )}
                <div className={styles.header}>
                    {inlineHeader && detailsHeader}
                    <div className={styles.reviews}>
                        <div className={styles.reviewsStarRating}>
                            <PdpSharedStarRating seller={seller} />
                        </div>
                        <PdpSharedDealerDetailsFeedback relay={relay} seller={seller} />
                    </div>
                    <span className={styles.location} data-tn="dealer-location">
                        <FormattedMessage
                            id="PdpSharedDealerDetailsHeader.location"
                            defaultMessage="Located in {dealerLocation}"
                            values={{ dealerLocation }}
                        />
                    </span>
                    {isClient && shouldShowBioModal(seller) && (
                        <PdpSharedDealerDetailsBioModalLazy
                            seller={seller}
                            linkClass={styles.learnMore}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

PdpSharedDealerDetailsHeaderComponent.propTypes = {
    inlineHeader: bool,
    isClient: bool,
    seller: object,
    user: object,
    relay: object,
};

export const PdpSharedDealerDetailsHeader = createFragmentContainer(
    PdpSharedDealerDetailsHeaderComponent,
    {
        user: graphql`
            fragment PdpSharedDealerDetailsHeader_user on User {
                canShowSellerName
            }
        `,
        seller: graphql`
            fragment PdpSharedDealerDetailsHeader_seller on Seller {
                ...PdpSharedStarRating_seller
                ...PdpSharedDealerDetailsFeedback_seller
                ...PdpSharedDealerDetailsBioModalLazy_seller
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
                sellerProfile {
                    company
                }
                shippingAddress {
                    displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
                }
                liveStorefrontProfile {
                    headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                        status
                        path
                    }
                    galleryAlbum {
                        mediaList {
                            element {
                                ... on Photo {
                                    path
                                }
                                ... on Video {
                                    url
                                }
                            }
                        }
                    }
                }
            }
        `,
    }
);
